import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { directoryEntries } from "@/data/directoryData";
import { EntryType } from "@/data/directoryTypes";
import DirectoryCard from "@/components/DirectoryCard";
import ExportDialog from "@/components/ExportDialog";
import { Search, SlidersHorizontal, X, Shield, Database, BookOpen, Users, Building2, GraduationCap, Landmark, Download, Settings, Menu } from "lucide-react";

const allTypes: EntryType[] = [
  "Regulator", "Regional Entity", "ISAC", "Standard", "Product",
  "Consultant", "Training", "Influencer", "Event", "Procurement", "Reference",
];

const allCategories = [...new Set(directoryEntries.map((e) => e.category))];

const categoryIcons: Record<string, React.ReactNode> = {
  "Regulators & Standards Bodies": <Landmark size={16} />,
  "ERO Enforcement Map": <Shield size={16} />,
  "Information Sharing & Threat Intel": <Database size={16} />,
  "Products — Compliance & Evidence": <BookOpen size={16} />,
  "Products — OT/ICS Integrity": <Shield size={16} />,
  "Products — Service Management": <Building2 size={16} />,
  "Standalone On-Prem Change Management": <Settings size={16} />,
  "Consultants & Advisors": <Users size={16} />,
  "Training Providers": <GraduationCap size={16} />,
  "Influencers & Authorities": <Users size={16} />,
  "Government & Procurement": <Landmark size={16} />,
};

const Index = () => {
  const [search, setSearch] = useState("");
  const [selectedTypes, setSelectedTypes] = useState<EntryType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "NERC CIP Directory — Regulators, Products, Consultants & Compliance Resources";
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "index-jsonld";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "NERC CIP Directory",
      description: "The definitive directory of NERC CIP regulators, regional entities, compliance products, consultants, training providers, and key influencers.",
      url: window.location.origin,
    });
    document.getElementById("index-jsonld")?.remove();
    document.head.appendChild(script);
    return () => { document.getElementById("index-jsonld")?.remove(); };
  }, []);

  const filtered = useMemo(() => {
    return directoryEntries.filter((entry) => {
      const matchesSearch =
        !search ||
        entry.name.toLowerCase().includes(search.toLowerCase()) ||
        entry.whyItMatters.toLowerCase().includes(search.toLowerCase()) ||
        entry.primaryJob.toLowerCase().includes(search.toLowerCase()) ||
        entry.cipAreas.some((a) => a.toLowerCase().includes(search.toLowerCase()));
      const matchesType = selectedTypes.length === 0 || selectedTypes.includes(entry.type);
      const matchesCategory = !selectedCategory || entry.category === selectedCategory;
      return matchesSearch && matchesType && matchesCategory;
    });
  }, [search, selectedTypes, selectedCategory]);

  const toggleType = (type: EntryType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedCategory(null);
    setSearch("");
  };

  const hasActiveFilters = selectedTypes.length > 0 || selectedCategory || search;

  const groupedEntries = useMemo(() => {
    const groups: Record<string, typeof filtered> = {};
    filtered.forEach((entry) => {
      if (!groups[entry.category]) groups[entry.category] = [];
      groups[entry.category].push(entry);
    });
    return groups;
  }, [filtered]);

  return (
    <div className="min-h-screen bg-background">
      {/* Nav bar */}
      <nav className="border-b border-border bg-card">
        <div className="container max-w-6xl py-2 px-4 flex items-center justify-between">
          <span className="text-[11px] font-mono text-muted-foreground tracking-wider uppercase">
            NERC CIP Ecosystem
          </span>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-4">
            <Link to="/what-is-nerc-cip" className="text-[11px] font-medium text-muted-foreground hover:text-primary transition-colors">
              What is NERC CIP?
            </Link>
            <Link to="/how-to-use" className="text-[11px] font-medium text-muted-foreground hover:text-primary transition-colors">
              How to Use
            </Link>
            <Link to="/cip-standards" className="text-[11px] font-medium text-muted-foreground hover:text-primary transition-colors">
              CIP Standards
            </Link>
            <a href="https://www.serviceaide.com" target="_blank" rel="noopener noreferrer" className="text-[11px] text-muted-foreground hover:text-primary transition-colors">
              Sponsored by <span className="font-semibold text-primary">serviceaide.com</span>
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border px-4 py-3 space-y-3 bg-card">
            <Link to="/what-is-nerc-cip" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              What is NERC CIP?
            </Link>
            <Link to="/how-to-use" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              How to Use
            </Link>
            <Link to="/cip-standards" onClick={() => setMobileMenuOpen(false)} className="block text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
              CIP Standards
            </Link>
            <a href="https://www.serviceaide.com" target="_blank" rel="noopener noreferrer" className="block text-sm text-muted-foreground hover:text-primary transition-colors">
              Sponsored by <span className="font-semibold text-primary">serviceaide.com</span>
            </a>
          </div>
        )}
      </nav>

      {/* Hero */}
      <div className="border-b border-border" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container max-w-6xl py-10 md:py-16 px-4">
          <div className="max-w-2xl">
            <h1 className="text-3xl md:text-5xl font-extrabold text-dark-foreground tracking-tight leading-[1.1]">
              NERC CIP
              <span className="block text-gradient">Directory</span>
            </h1>
            <p className="text-dark-foreground/60 mt-4 text-base leading-relaxed max-w-lg">
              The definitive guide to regulators, products, consultants, and key players across the NERC CIP compliance ecosystem.
            </p>

            {/* Stats */}
            <div className="flex items-center gap-6 mt-6">
              <div>
                <span className="text-2xl font-bold text-dark-foreground">{directoryEntries.length}</span>
                <span className="text-xs text-dark-foreground/50 ml-1.5">Entries</span>
              </div>
              <div className="w-px h-8 bg-dark-foreground/15" />
              <div>
                <span className="text-2xl font-bold text-dark-foreground">{allCategories.length}</span>
                <span className="text-xs text-dark-foreground/50 ml-1.5">Categories</span>
              </div>
              <div className="w-px h-8 bg-dark-foreground/15" />
              <div>
                <span className="text-2xl font-bold text-dark-foreground">{allTypes.length}</span>
                <span className="text-xs text-dark-foreground/50 ml-1.5">Types</span>
              </div>
            </div>

            {/* Search */}
            <div className="mt-6 md:mt-8 space-y-2 md:space-y-0 md:flex md:gap-2">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search entries, CIP areas..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all shadow-sm"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowExport(true)}
                  className="flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-all shadow-sm"
                >
                  <Download size={16} />
                  <span className="hidden sm:inline">Export</span>
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-5 py-3 rounded-lg border text-sm font-medium transition-all shadow-sm ${
                    showFilters || hasActiveFilters
                      ? "bg-primary border-primary text-primary-foreground"
                      : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-silver"
                  }`}
                >
                  <SlidersHorizontal size={16} />
                  <span className="hidden sm:inline">Filters</span>
                  {(selectedTypes.length > 0 || selectedCategory) && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-primary-foreground/20 text-[10px] font-mono">
                      {selectedTypes.length + (selectedCategory ? 1 : 0)}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="border-b border-border bg-card">
          <div className="container max-w-6xl py-5 px-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Filter by Type</span>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-primary hover:underline flex items-center gap-1">
                  <X size={12} /> Clear all
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {allTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                    selectedTypes.includes(type)
                      ? "bg-foreground text-dark-foreground border-foreground"
                      : "bg-card text-secondary-foreground border-border hover:border-silver"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            <div>
              <span className="text-xs font-semibold text-foreground uppercase tracking-wider">Filter by Category</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {allCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                      selectedCategory === cat
                        ? "bg-foreground text-dark-foreground border-foreground"
                        : "bg-card text-secondary-foreground border-border hover:border-silver"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results bar */}
      <div className="border-b border-border">
        <div className="container max-w-6xl py-3 px-4 flex items-center gap-6 text-xs text-muted-foreground">
          <span>
            Showing <span className="font-semibold text-foreground">{filtered.length}</span> of {directoryEntries.length} entries
          </span>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="text-primary hover:underline">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Directory grid */}
      <div className="container max-w-6xl py-10 px-4 space-y-10">
        {Object.entries(groupedEntries).map(([category, entries]) => (
          <section key={category}>
            <div className="flex items-center gap-3 mb-5">
              <span className="text-primary">{categoryIcons[category] || <Shield size={16} />}</span>
              <h2 className="text-sm font-bold text-foreground uppercase tracking-wider">{category}</h2>
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs font-mono text-muted-foreground bg-secondary px-2 py-0.5 rounded">{entries.length}</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {entries.map((entry) => (
                <DirectoryCard key={entry.id} entry={entry} />
              ))}
            </div>
          </section>
        ))}

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-sm">No entries match your filters.</p>
            <button onClick={clearFilters} className="text-primary text-sm mt-2 hover:underline">
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-8 bg-card">
        <div className="container max-w-6xl px-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground font-mono">
            NERC CIP Directory — {directoryEntries.length} entries
          </span>
          <a
            href="https://www.serviceaide.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            Sponsored by <span className="font-semibold text-primary">serviceaide.com</span>
          </a>
        </div>
      </footer>

      <ExportDialog open={showExport} onOpenChange={setShowExport} entries={filtered} />
    </div>
  );
};

export default Index;
